import { useNavigate } from "react-router-dom";
import "./index.scss";
import { CardGroup, SmallCard } from "../card-layout";
import { DeleteOutlined, CloseOutlined } from "@ant-design/icons";
import { useOrganizationTemplates } from "@/store/hooks/use-templates";
import { Spin, Typography, Popconfirm, message, Tag, Button } from "antd";
import { useState, useEffect } from "react";
import {
  deleteOrganizationTemplate,
  OrganizationTemplateItem,
  ListCommunityTemplates,
  CommunityTemplateItem,
  deleteCommunityTemplateById,
} from "@/api/weave-x";
import { useAppSelector, useAppDispatch } from "@/store/hooks";
import { selectUser } from "@/store/features/userinfo/userinfoSlice";
import { fetchInvestorsAllThunk } from "@/store/features/investors/investorsThunk";
import {
  selectInvestors,
  selectInvestorsLoadingState,
} from "@/store/features/investors/investorsSlice";
import { isVisibleWavvestTemplate } from "./wavvest-template-visibility";

function Studio() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { templates, isLoading, isLoadingMore, hasMore, refetch, loadMore } =
    useOrganizationTemplates();
  const [wavvestTemplates, setWavvestTemplates] = useState<
    CommunityTemplateItem[]
  >([]);
  const [wavvestLoading, setWavvestLoading] = useState(false);

  const user = useAppSelector(selectUser) || {};
  const { client_id: clientId, id: userId } = user;
  const investors = useAppSelector(selectInvestors);
  const investorsLoadingState = useAppSelector(selectInvestorsLoadingState);

  useEffect(() => {
    if (investors.length === 0 && investorsLoadingState === "idle") {
      dispatch(fetchInvestorsAllThunk());
    }
  }, [investors.length, investorsLoadingState, dispatch]);

  useEffect(() => {
    const fetchWavvestTemplates = async () => {
      setWavvestLoading(true);
      try {
        const response = await ListCommunityTemplates({
          limit: 50,
        });
        setWavvestTemplates(response.templates.filter(isVisibleWavvestTemplate));
      } catch (error) {
        console.error("Error fetching wavvest templates:", error);
      } finally {
        setWavvestLoading(false);
      }
    };

    fetchWavvestTemplates();
  }, []);

  const getPopularityTag = (template: CommunityTemplateItem) => {
    // Check if template was created within last 7 days (createdAt is in seconds, convert to milliseconds)
    const now = Date.now();
    const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
    const createdAtMs = template.createdAt * 1000; // Convert seconds to milliseconds
    const isNew = createdAtMs > sevenDaysAgo;

    if (isNew) {
      return <Tag color="blue">🆕 New</Tag>;
    }

    const usageCount = template.usageCount;
    if (usageCount >= 100) {
      return <Tag color="red">🔥 Hot</Tag>;
    } else if (usageCount >= 50) {
      return <Tag color="orange">⭐ Popular</Tag>;
    } else if (usageCount >= 20) {
      return <Tag color="green">👍 Trending</Tag>;
    } else if (usageCount >= 10) {
      return <Tag color="purple">✨ Rising</Tag>;
    }
    return null;
  };

  const handleUseCustomizedTemplate = (template: OrganizationTemplateItem) => {
    return () => {
      navigate("/home/planning-templates/customized-template-preview", {
        state: {
          template,
        },
      });
    };
  };

  const handleDeleteTemplate = (templateId: string) => {
    const deleteTemplate = async () => {
      try {
        await deleteOrganizationTemplate(clientId, templateId);
        message.success("Template deleted successfully");
        refetch();
      } catch (err) {
        console.error("Error deleting template:", err);
        message.error("Failed to delete template");
      }
    };

    return (
      <Popconfirm
        title="Delete template"
        description="Are you sure you want to delete this template? This action cannot be undone."
        okText="Yes"
        cancelText="No"
        onConfirm={deleteTemplate}
        okButtonProps={{ danger: true }}
      >
        <button className="template-card-delete" aria-label="Delete template">
          <CloseOutlined />
        </button>
      </Popconfirm>
    );
  };

  const handleDeleteWavvestTemplate = (template: CommunityTemplateItem) => {
    const deleteWavvestTemplate = async () => {
      try {
        await deleteCommunityTemplateById(template.templateId);
        message.success("Wavvest template deleted successfully");
        // Refresh wavvest templates list
        const response = await ListCommunityTemplates({
          limit: 50,
        });
        setWavvestTemplates(response.templates.filter(isVisibleWavvestTemplate));
      } catch (err) {
        console.error("Error deleting wavvest template:", err);
        message.error("Failed to delete wavvest template");
      }
    };

    return (
      <Popconfirm
        title="Delete wavvest template"
        description="Are you sure you want to delete this wavvest template? This action cannot be undone."
        okText="Yes"
        cancelText="No"
        onConfirm={deleteWavvestTemplate}
        okButtonProps={{ danger: true }}
      >
        <button
          className="template-card-delete"
          aria-label="Delete wavvest template"
        >
          <CloseOutlined />
        </button>
      </Popconfirm>
    );
  };

  const canDeleteWavvestTemplate = (
    template: CommunityTemplateItem,
  ): boolean => {
    // For anonymous templates (no originalPublisher or no advisorId), don't show delete button
    if (!template.originalPublisher || !template.originalPublisher.advisorId) {
      return false;
    }
    // Only the original publisher can delete the wavvest template
    return template.originalPublisher.advisorId === userId;
  };

  const renderCustomizedTemplatesSection = () => {
    if (isLoading) {
      return <Spin />;
    }
    if (!templates.length) {
      return null;
    }

    return (
      <CardGroup
        title={"Templates created by your organization"}
        className="card-group-1"
      >
        {templates.map((template) => {
          const title = (
            <div>
              <Typography.Paragraph
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  fontSize: "18px",
                }}
              >
                {template.templateName}
                {handleDeleteTemplate(template.templateId!)}
              </Typography.Paragraph>
            </div>
          );
          return (
            <SmallCard
              key={template.templateId}
              title={title}
              description={template.templateDescription}
              buttonText={"Use This Template"}
              onButtonClick={handleUseCustomizedTemplate(template)}
            />
          );
        })}
        {hasMore && (
          <div style={{ textAlign: "center", marginTop: "20px" }}>
            <Button
              type="primary"
              loading={isLoadingMore}
              onClick={loadMore}
              disabled={isLoadingMore}
            >
              {isLoadingMore ? "Loading..." : "Load More"}
            </Button>
          </div>
        )}
      </CardGroup>
    );
  };

  return (
    <div className="studio-container">
      <CardGroup title={"Wavvest Templates"} className="card-group-1">
        {wavvestLoading ? (
          <Spin />
        ) : wavvestTemplates.length > 0 ? (
          wavvestTemplates.map((template: CommunityTemplateItem) => {
            const popularityTag = getPopularityTag(template);
            const titleWithTag = (
              <div>
                <Typography.Paragraph
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    fontSize: "18px",
                    marginBottom: "0",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span>{template.displayName}</span>
                    {popularityTag}
                  </div>
                  {canDeleteWavvestTemplate(template) &&
                    handleDeleteWavvestTemplate(template)}
                </Typography.Paragraph>
              </div>
            );

            const enhancedDescription = template.displayDescription;

            return (
              <SmallCard
                key={template.templateId}
                title={titleWithTag}
                description={enhancedDescription}
                buttonText={"Use This Template"}
                onButtonClick={() => {
                  navigate(
                    "/home/planning-templates/customized-template-preview",
                    {
                      state: {
                        template: {
                          templateId: template.templateId,
                          templateName: template.displayName,
                          templateDescription: template.displayDescription,
                          templateSourceCode: template.templateSourceCode,
                          clientId: template.originalPublisher?.clientId,
                        },
                      },
                    },
                  );
                }}
              />
            );
          })
        ) : (
          <div>No wavvest templates available</div>
        )}
      </CardGroup>

      {renderCustomizedTemplatesSection()}
    </div>
  );
}

export default Studio;
